export const INTERSTITIAL_NAV_COUNT_KEY = "bfc_interstitial_nav_count";
export const INTERSTITIAL_LAST_SHOWN_KEY = "bfc_interstitial_last_shown_at";
export const SESSION_ENTRY_PATH_KEY = "bfc_session_entry_path";
export const SESSION_ROUTE_VISITS_KEY = "bfc_session_route_visits";

const BLOCKED_PATH_PREFIXES = [
  "/privacy",
  "/terms",
  "/legal",
  "/contact",
  "/robots.txt",
  "/sitemap",
  "/api",
];

function readNumber(key: string, fallback = 0) {
  if (typeof window === "undefined") return fallback;

  const value =
    window.sessionStorage.getItem(key) ?? window.localStorage.getItem(key);
  const parsed = Number(value);

  return Number.isFinite(parsed) ? parsed : fallback;
}

function writeSessionNumber(key: string, value: number) {
  if (typeof window === "undefined") return;
  window.sessionStorage.setItem(key, String(value));
}

function writeLocalNumber(key: string, value: number) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(key, String(value));
}

export function trackSessionRouteVisit(pathname: string) {
  if (typeof window === "undefined") return;

  if (!window.sessionStorage.getItem(SESSION_ENTRY_PATH_KEY)) {
    window.sessionStorage.setItem(SESSION_ENTRY_PATH_KEY, pathname || "/");
    window.sessionStorage.setItem("bfc_current_path", pathname || "/");
    writeSessionNumber(SESSION_ROUTE_VISITS_KEY, 0);
    return;
  }

  const currentPath = window.sessionStorage.getItem("bfc_current_path");
  if (currentPath === pathname) return;

  window.sessionStorage.setItem("bfc_current_path", pathname);
  writeSessionNumber(
    SESSION_ROUTE_VISITS_KEY,
    readNumber(SESSION_ROUTE_VISITS_KEY) + 1
  );
}

export function isBlockedInterstitialPath(pathname: string) {
  return BLOCKED_PATH_PREFIXES.some((prefix) =>
    pathname === prefix || pathname.startsWith(`${prefix}/`)
  );
}

export function normalizeInternalHref(href: string) {
  if (typeof window === "undefined") return null;

  try {
    const url = new URL(href, window.location.href);
    const currentUrl = new URL(window.location.href);

    if (url.origin !== currentUrl.origin) return null;
    if (url.protocol !== "http:" && url.protocol !== "https:") return null;

    const sameDocument =
      url.pathname === currentUrl.pathname &&
      url.search === currentUrl.search;

    if (sameDocument) return null;
    if (isBlockedInterstitialPath(url.pathname)) return null;

    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return null;
  }
}

export function incrementInterstitialNavigationCount() {
  const nextCount = readNumber(INTERSTITIAL_NAV_COUNT_KEY) + 1;
  writeSessionNumber(INTERSTITIAL_NAV_COUNT_KEY, nextCount);
  return nextCount;
}

export function markInterstitialShown(now = Date.now()) {
  writeLocalNumber(INTERSTITIAL_LAST_SHOWN_KEY, now);
}

export function shouldShowInterstitial({
  navigationCount,
  minNavigations,
  repeatNavigations,
  cooldownMs,
  now = Date.now(),
}: {
  navigationCount: number;
  minNavigations: number;
  repeatNavigations: number;
  cooldownMs: number;
  now?: number;
}) {
  if (navigationCount < minNavigations) return false;

  const normalizedRepeat = Math.max(1, repeatNavigations);
  const isScheduledNavigation =
    navigationCount === minNavigations ||
    (navigationCount - minNavigations) % normalizedRepeat === 0;

  if (!isScheduledNavigation) return false;

  const lastShownAt = readNumber(INTERSTITIAL_LAST_SHOWN_KEY);
  if (cooldownMs > 0 && lastShownAt > 0 && now - lastShownAt < cooldownMs) {
    return false;
  }

  return true;
}
